import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'

import { cn } from '@/lib/cn'

type Tone = 'info' | 'success' | 'warning' | 'error'

const TONES: Record<Tone, { box: string; icon: ReactNode }> = {
  info: {
    box: 'bg-primary/5 text-on-surface border-primary/20',
    icon: <Info size={18} className="text-primary" />,
  },
  success: {
    box: 'bg-success-container/40 text-on-success-container border-success/20',
    icon: <CheckCircle2 size={18} className="text-success" />,
  },
  warning: {
    box: 'bg-warning-container/40 text-on-warning-container border-warning/20',
    icon: <AlertTriangle size={18} className="text-warning" />,
  },
  error: {
    box: 'bg-error-container/40 text-on-error-container border-error/20',
    icon: <XCircle size={18} className="text-error" />,
  },
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: Tone
  title?: string
  children: ReactNode
  className?: string
}) {
  const styles = TONES[tone]

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-lg border px-4 py-3 text-sm', styles.box, className)}
    >
      <span className="mt-0.5 shrink-0" aria-hidden="true">
        {styles.icon}
      </span>
      <div className="space-y-1">
        {title && <p className="font-bold">{title}</p>}
        <div className="leading-relaxed">{children}</div>
      </div>
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <span
        className="rounded-full bg-surface-container-low p-4 text-on-surface-variant"
        aria-hidden="true"
      >
        {icon}
      </span>
      <h3 className="font-headline text-lg font-bold text-secondary">{title}</h3>
      <p className="max-w-sm text-sm text-on-surface-variant">{description}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}
